import { createActionHandler, createReducer, OperationStatus } from '@pressreader/appstore';
import { userChangeDone } from '@pressreader/authentication-core';
import { defaultIssueContentRestrictions, IPublicationContentRestrictions, IssueContentRestrictions } from '@pressreader/content-types';
import { purchaseDone } from '@pressreader/purchase';

import {
    issueRestrictionsUpdated,
    loadGlobalRestrictionsBegan,
    loadGlobalRestrictionsDone,
    loadGlobalRestrictionsFailed,
    loadIssueRestrictionsBegan,
    loadIssueRestrictionsDone,
    loadIssueRestrictionsFailed,
} from '../actions/restrictions';

interface State {
    publications: {
        status: OperationStatus;
        restrictions: { [cid: string]: IPublicationContentRestrictions };
    };

    issues: {
        [cidDateKey: string]: {
            status: OperationStatus;
            restrictions: IssueContentRestrictions;
        };
    };
}

const initialState: State = {
    publications: {
        status: OperationStatus.None,
        restrictions: {},
    },
    issues: {},
};

function getIssueRestrictions(state: State, cidDateKey: string) {
    if (state.issues[cidDateKey]) {
        return state.issues[cidDateKey];
    }

    return { status: OperationStatus.None, restrictions: { ...defaultIssueContentRestrictions } };
}

function mergeRestrictions(
    base: IPublicationContentRestrictions | undefined,
    updates: Partial<IPublicationContentRestrictions>,
): IPublicationContentRestrictions {
    const merged = {
        anonymousContentRestricted: updates.anonymousContentRestricted || (base?.anonymousContentRestricted ?? false),
        sound: updates.sound || (base?.sound ?? false),
        blogging: updates.blogging || (base?.blogging ?? false),
        comments: updates.comments || (base?.comments ?? false),
        emailSharing: updates.emailSharing || (base?.emailSharing ?? false),
        readItLater: updates.readItLater || (base?.readItLater ?? false),
        smartFlow: updates.smartFlow || (base?.smartFlow ?? false),
        translation: updates.translation || (base?.translation ?? false),
    };

    return merged;
}

const reducer = createReducer(
    initialState,
    createActionHandler(userChangeDone, state => ({
        ...state,
        publications: { status: OperationStatus.None, restrictions: {} },
        issues: {},
    })),
    createActionHandler(purchaseDone, state => ({
        ...state,
        publications: { status: OperationStatus.None, restrictions: {} },
        issues: {},
    })),
    createActionHandler(loadGlobalRestrictionsBegan, state => ({
        ...state,
        publications: {
            ...state.publications,
            status: OperationStatus.Pending,
        },
    })),
    createActionHandler(loadGlobalRestrictionsDone, (state, { payload }) => ({
        ...state,
        publications: {
            ...state.publications,
            status: OperationStatus.Done,
            restrictions: payload.restrictions,
        },
    })),
    createActionHandler(issueRestrictionsUpdated, (state, { payload }) => ({
        ...state,
        publications: {
            ...state.publications,
            status: OperationStatus.Done,
            restrictions: {
                ...state.publications.restrictions,
                [payload.cid]: mergeRestrictions(state.publications.restrictions[payload.cid], payload.restrictions),
            },
        },
    })),
    createActionHandler(loadGlobalRestrictionsFailed, state => ({
        ...state,
        publications: {
            ...state.publications,
            status: OperationStatus.Failed,
        },
    })),
    createActionHandler(loadIssueRestrictionsBegan, (state, { payload }) => {
        const issueRestrictions = { ...getIssueRestrictions(state, payload.cidDateKey) };
        issueRestrictions.status = OperationStatus.Pending;

        return {
            ...state,
            issues: {
                ...state.issues,
                [payload.cidDateKey]: issueRestrictions,
            },
        };
    }),
    createActionHandler(loadIssueRestrictionsDone, (state, { payload }) => {
        const issueRestrictions = { ...getIssueRestrictions(state, payload.cidDateKey) };
        issueRestrictions.status = OperationStatus.Done;
        issueRestrictions.restrictions = payload.restrictions;

        return {
            ...state,
            issues: {
                ...state.issues,
                [payload.cidDateKey]: issueRestrictions,
            },
        };
    }),
    createActionHandler(loadIssueRestrictionsFailed, (state, { payload }) => {
        const issueRestrictions = { ...getIssueRestrictions(state, payload.cidDateKey) };
        issueRestrictions.status = OperationStatus.Failed;

        return {
            ...state,
            issues: {
                ...state.issues,
                [payload.cidDateKey]: issueRestrictions,
            },
        };
    }),
);

export { reducer, State };
